import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_POS = { x: 0, y: 0 };

const isInteractiveElement = (target) => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(
    target.closest(
      "button, a, input, textarea, select, option, label, [role='button'], [data-no-drag='true']",
    ),
  );
};

const useDialogDrag = (isOpen, options = {}) => {
  const [position, setPosition] = useState(DEFAULT_POS);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, dialogX: 0, dialogY: 0 });
  const { baseTransform } = options;

  const onMouseMove = useCallback((event) => {
    setPosition({
      x: dragStartRef.current.dialogX + (event.clientX - dragStartRef.current.mouseX),
      y: dragStartRef.current.dialogY + (event.clientY - dragStartRef.current.mouseY),
    });
  }, []);

  const onDragHandleMouseDown = useCallback(
    (event) => {
      if (event.button !== 0 || isInteractiveElement(event.target)) {
        return;
      }

      event.preventDefault();
      dragStartRef.current = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        dialogX: position.x,
        dialogY: position.y,
      };

      setIsDragging(true);
    },
    [position.x, position.y],
  );

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMouseMove = (event) => onMouseMove(event);
    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, onMouseMove]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(DEFAULT_POS);
      setIsDragging(false);
    }
  }, [isOpen]);

  const hasOffset = position.x !== 0 || position.y !== 0;

  return {
    isDragging,
    dragStyle: hasOffset
      ? {
        transform: baseTransform
          ? `${baseTransform} translate(${position.x}px, ${position.y}px)`
          : `translate(${position.x}px, ${position.y}px)`,
      }
      : undefined,
    onDragHandleMouseDown,
  };
};

export default useDialogDrag;

